import React from 'react';
import styled from 'styled-components';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import { motion } from 'framer-motion';
import { Link } from 'gatsby';

const CaseStudies = ({ data }) => {
  return (
    <Container>
      <HeaderSection>
        <SectionTitle>Relevant Case Studies</SectionTitle>
      </HeaderSection>
      <List>
        {data.map((caseStudy, index) => {
          const { previewImage, company, whatWeDid, alt, slug } = caseStudy;
          return (
            <PreviewLink to={`/our-work/${slug.current}`}>
              <CaseStudyPreview
                key={index}
                variants={caseStudyPreviewVariants}
                whileHover="hover"
                whileTap="tap"
                layout>
                <ImageContainer>
                  <GatsbyImage image={getImage(previewImage.asset.gatsbyImageData)} alt={alt} />
                </ImageContainer>
                <ContentContainer>
                  <motion.h4 variants={caseStudyPreviewVariants}>{company}</motion.h4>
                  <motion.p className="description" variants={caseStudyPreviewVariants}>
                    {whatWeDid}
                  </motion.p>
                  <SeeMore className="read-more" variants={caseStudyPreviewVariants}>
                    See case study
                  </SeeMore>
                </ContentContainer>
              </CaseStudyPreview>
            </PreviewLink>
          );
        })}
      </List>
      <SeeAll to="/our-work">See All Our Work</SeeAll>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  @media screen and (min-width: 768px) {
    margin: 3rem 0;
  }
`;

const HeaderSection = styled.div`
  margin-bottom: 1.5rem;
  @media screen and (min-width: 768px) {
    margin-bottom: 3rem;
  }
`;

const SectionTitle = styled.h3`
  font-weight: 900;
  line-height: 1.1;
  font-size: 3rem;
  margin-bottom: 1rem;

  @media screen and (min-width: 768px) {
    font-size: 3.5rem;
  }
`;

const List = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  height: 100%;
  @media only screen and (max-width: 30rem) {
    flex-direction: column;
    > a:first-of-type {
      margin-bottom: 2.75rem;
    }
  }
`;

export const CaseStudyPreview = styled(motion.div)`
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: flex-start;
  width: 100%;
  position: relative;
  overflow: hidden;
  border-radius: 2px;
  transition: all 0.3s ease;
  padding: 0.5rem;

  /* Background fill animation */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    right: 100%;
    width: 100%;
    height: 100%;
    background: var(--ax-black);
    transition: right 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    z-index: 1;
  }

  &:hover::before {
    right: 0;
  }

  /* Text elements positioning */
  h4,
  .description,
  .read-more {
    position: relative;
    z-index: 2;
    transition: all 0.4s ease;
  }

  h4 {
    color: var(--ax-black);
    font-size: 2rem;
    font-weight: 300;
    margin-top: 0.5rem;
  }

  .description {
    font-size: 0.75rem;
    margin: 0.5rem 0 1.25rem 0;
    color: var(--ax-black);
    opacity: 1;
    transform: translateY(0);
    transition: opacity 0.3s ease 0.1s, transform 0.3s ease 0.1s;
  }

  .read-more {
    opacity: 0;
    transition: opacity 0.3s ease 0.2s, transform 0.3s ease 0.2s;
    position: absolute;
    bottom: 1rem;
    left: 0.5rem;
    color: white;
    font-size: 0.875rem;
  }

  /* Hover states */
  &:hover h4 {
    color: white;
    margin-left: 0.5rem;
    transition: all 0.3s ease 0.2s;
  }

  &:hover .description {
    opacity: 0;
    transform: translateY(-10px);
    transition: opacity 0.2s ease, transform 0.2s ease;
  }

  &:hover .read-more {
    opacity: 1;
    transition: opacity 0.3s ease 0.3s, transform 0.3s ease 0.3s;
  }

  @media only screen and (max-width: 48rem) {
    h4 {
      font-size: 1.25rem;
    }
  }
`;

export const PreviewLink = styled(Link)`
  color: var(--ax-black);
  height: auto;
  text-decoration: none;
  width: calc(100% - 5rem);
  margin-right: 3rem;
  &:last-child {
    margin-right: 0;
  }
  @media only screen and (max-width: 48rem) {
    width: calc(100% - 1rem);
    margin-right: 1rem;
    &:last-child {
      margin-right: 0;
    }
  }
`;

export const ImageContainer = styled.div`
  position: relative;
  z-index: 2;
  margin-bottom: 1rem;

  .gatsby-image-wrapper {
    width: 100%;
    border-radius: 6px;
  }
`;

export const ContentContainer = styled.div`
  position: relative;
  z-index: 2;
  flex: 1;
`;

export const SeeMore = styled(motion.div)`
  display: flex;
  color: var(--ax-black);
  flex-direction: column;
  font-weight: 700;
  margin-top: auto;
  text-transform: uppercase;
  width: 100%;
`;

const SeeAll = styled(Link)`
  align-items: center;
  border-radius: 0.125em;
  display: inline-flex;
  font-size: 1rem;
  font-weight: 700;
  height: 3em;
  justify-content: center;
  transition: background-color 150ms ease-in-out, color 150ms ease-in-out;
  pointer-events: auto;
  text-decoration: none;
  text-transform: uppercase;
  width: 10.375em;
  margin: 2rem auto;
  &:hover {
    background-color: var(--ax-gold);
    color: #fff;

    :active {
      border: 0.063em solid #000;
      background-color: #000;
      color: #fff;
      transition: background-color 75ms ease-in-out, border 75ms ease-in-out, color 75ms ease-in-out;
    }

    :not(:active) {
      border: none;
      transition: background-color 75ms ease-in-out, border 75ms ease-in-out, color 75ms ease-in-out;
    }
  }

  &:not(:hover) {
    :visited {
      color: var(--ax-gold);
    }

    :not(:active) {
      border: 0.063em solid var(--ax-gold);
      background-color: transparent;
      color: var(--ax-gold);
      transition: background-color 150ms ease-in-out, border 150ms ease-in-out;
    }
  }
`;

export const caseStudyPreviewVariants = {
  hover: {
    cursor: 'pointer',
    scale: 1.02,
    transition: {
      type: 'tween',
      ease: 'easeInOut',
      duration: 0.3
    }
  }
};

export default CaseStudies;
